import { Checkbox, Table, Tag } from 'antd';
import React, { useMemo } from 'react';
import { parsePrometheeResourceGroupName } from '../../../utils/prometheeGroups';

const ACTIONS = ['Read', 'Create', 'Update', 'Delete'];

/**
 * Matrice "Ressource x Action" (cases à cocher).
 *
 * Props :
 *  - allGroups            : Group[] brut (groupes fins Promethee:<Action>:<Resource>)
 *  - selectedGroupIds     : number[] — IDs des groupes fins sélectionnés
 *  - onChange             : (number[]) => void
 *  - coveredByGlobalRole  : null | 'Read' | 'Update' — grise les cases déjà
 *                           couvertes par un rôle global coché (informatif)
 */
const PermissionMatrix = ({
  allGroups = [],
  selectedGroupIds = [],
  onChange,
  coveredByGlobalRole = null,
}) => {
  // { Resource: { Read: groupId, Create: groupId, Update: groupId, Delete: groupId } }
  const matrix = useMemo(() => {
    const out = {};
    for (const g of allGroups) {
      const match = parsePrometheeResourceGroupName(g.name);
      if (!match) continue;
      const [, action, resource] = match;
      if (!out[resource]) out[resource] = {};
      out[resource][action] = g.id;
    }
    return out;
  }, [allGroups]);

  const resources = useMemo(() => Object.keys(matrix).sort(), [matrix]);
  const selectedSet = useMemo(
    () => new Set(selectedGroupIds),
    [selectedGroupIds]
  );

  const isCoveredByGlobal = (action) => {
    if (coveredByGlobalRole === 'Update' && action !== 'Delete') return true;
    if (coveredByGlobalRole === 'Read' && action === 'Read') return true;
    return false;
  };

  const toggle = (groupId) => {
    const next = new Set(selectedSet);
    if (next.has(groupId)) next.delete(groupId);
    else next.add(groupId);
    onChange?.(Array.from(next));
  };

  const columns = [
    { title: 'Ressource', dataIndex: 'resource', width: 200 },
    ...ACTIONS.map((action) => ({
      title: action,
      align: 'center',
      width: 80,
      render: (_, { resource }) => {
        const gid = matrix[resource]?.[action];
        if (!gid) return null;
        const covered = isCoveredByGlobal(action);
        return (
          <Checkbox
            checked={selectedSet.has(gid) || covered}
            disabled={covered}
            onChange={() => toggle(gid)}
          />
        );
      },
    })),
  ];

  const dataSource = resources.map((r) => ({ key: r, resource: r }));

  return (
    <>
      {coveredByGlobalRole && (
        <Tag color="blue" style={{ marginBottom: 8 }}>
          Les cases grisées sont déjà couvertes par le rôle global{' '}
          <strong>Promethee:{coveredByGlobalRole}</strong>
        </Tag>
      )}
      <Table
        size="small"
        bordered
        pagination={false}
        scroll={{ y: 360 }}
        columns={columns}
        dataSource={dataSource}
      />
    </>
  );
};

export default PermissionMatrix;
